import type { EnvironmentId, ScopedThreadRef, ThreadId } from "@amoreg/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Queue from "effect/Queue";
import type * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";

import { threadKey } from "./entities.ts";

/**
 * A channel from code outside a thread's state machine (a UI action, a
 * command) to the one live machine for that thread.
 *
 * The machine listens for as long as its scope lives; a successor machine for
 * the same thread simply takes the channel over. Requests coalesce: a machine
 * that is busy sees one pending request, however many were made.
 */
export interface ThreadRequestChannel {
  /** The requests made for `ref` while the calling scope is open. */
  readonly requests: (
    ref: ScopedThreadRef,
  ) => Effect.Effect<Stream.Stream<void>, never, Scope.Scope>;
  /** Makes a request of the live machine for `ref`; false when there is none. */
  readonly request: (ref: ScopedThreadRef) => boolean;
}

export function makeThreadRequestChannel(): ThreadRequestChannel {
  const listeners = new Map<string, () => void>();
  return {
    requests: Effect.fn("ThreadRequestChannel.requests")(function* (ref) {
      const key = threadKey(ref);
      const pending = yield* Queue.sliding<void>(1);
      const listener = () => {
        Queue.offerUnsafe(pending, undefined);
      };
      yield* Effect.acquireRelease(
        Effect.sync(() => listeners.set(key, listener)),
        () =>
          Effect.sync(() => {
            // A successor may already have taken the channel over.
            if (listeners.get(key) === listener) listeners.delete(key);
          }),
      );
      return Stream.fromQueue(pending);
    }),
    request: (ref) => {
      const listener = listeners.get(threadKey(ref));
      listener?.();
      return listener !== undefined;
    },
  };
}

const olderTurnRequests = makeThreadRequestChannel();
const resubscribeRequests = makeThreadRequestChannel();

/**
 * "Load the next older page", from the UI to the thread machine. Resolved from
 * the Effect environment (overridable in tests); the default instance is
 * shared with the synchronous {@link requestOlderThreadTurns} entry point so
 * the apps get working wiring without providing anything.
 */
export class ThreadOlderTurnRequests extends Context.Reference<ThreadRequestChannel>(
  "@amoreg/client-runtime/state/threads/ThreadOlderTurnRequests",
  { defaultValue: () => olderTurnRequests },
) {}

/**
 * Asks the live state machine for `threadId` to fetch the next older page.
 * Returns false when no machine is live; callers render from
 * `EnvironmentThreadState.page` and can treat false as "nothing to do".
 */
export function requestOlderThreadTurns(environmentId: EnvironmentId, threadId: ThreadId): boolean {
  return olderTurnRequests.request({ environmentId, threadId });
}

/**
 * "The thread you are waiting for exists now", from the commands that create
 * a thread to its machine.
 *
 * A view opened on a draft subscribes to a thread the server does not have
 * yet. Every "not found" doubles the wait before the next attempt, up to a
 * slow poll, which is right for a thread that was deleted or is forbidden and
 * wrong the moment this client creates it: nothing told the machine, so the
 * view sat out its backoff (up to thirty seconds) while the reply streamed and
 * finished, and the sidebar, fed by the shell subscription, already showed it.
 */
export class ThreadResubscribeRequests extends Context.Reference<ThreadRequestChannel>(
  "@amoreg/client-runtime/state/threads/ThreadResubscribeRequests",
  { defaultValue: () => resubscribeRequests },
) {}
